/**
 * Utilities for handling chat route navigation and parameter stripping
 */

// Chat route patterns that should have their chat IDs stripped
const CHAT_ROUTE_PATTERNS = [
  /^\/chat\/[^\/]+$/,                    // /chat/[id]
  /^\/home\/chat\/[^\/]+$/,              // /home/chat/[id]  
  /^\/draft\/chat\/[^\/]+$/,             // /draft/chat/[id]
]

/**
 * Detects if a pathname is a chat route with an ID parameter
 */
export function isChatRoute(pathname: string): boolean {
  return CHAT_ROUTE_PATTERNS.some(pattern => pattern.test(pathname))
}

/**
 * Extracts the chat ID from a chat route path
 * Returns null if the path is not a chat route or has no ID
 */
export function getChatIdFromPath(pathname: string): string | null {
  for (const pattern of CHAT_ROUTE_PATTERNS) {
    const match = pathname.match(pattern)
    if (match) {
      // Extract the last segment which should be the chat ID
      const segments = pathname.split('/')
      return segments[segments.length - 1] || null
    }
  }
  return null
}

/**
 * Converts a chat route to its base path by removing the chat ID
 * Examples: 
 * - /chat/123 → /
 * - /home/chat/123 → /home
 * - /admin/history/chat/123 → /admin/history
 */
export function getBasePath(pathname: string): string {
  if (!isChatRoute(pathname)) {
    return pathname
  }

  // Remove the chat ID segment from the path
  const segments = pathname.split('/').filter(Boolean)
  
  // Find the 'chat' segment and remove it and everything after
  const chatIndex = segments.findIndex(segment => segment === 'chat')
  if (chatIndex !== -1) {
    const basePath = '/' + segments.slice(0, chatIndex).join('/')
    // Clean up double slashes and ensure proper root path
    return basePath === '/' || basePath === '' ? '/' : basePath
  }

  return pathname
}

/**
 * Determines if the target URL is navigating to a specific chat
 * This is used for the "preserve by exception" approach
 */
export function isNavigatingToChat(targetUrl: string): boolean {
  // Extract the path part from the URL (remove query params)
  const [targetPath] = targetUrl.split('?')
  
  // Chat routes should preserve context
  if (isChatRoute(targetPath)) {
    return true
  }
  
  // Template creation from chat messages should also preserve chat context
  if (targetPath.startsWith('/settings/templates/new-from-user-message')) {
    return true
  }
  
  return false
}

/**
 * Determines if chat params should be preserved during navigation
 * Uses "preserve by exception" logic: preserve when going to chat routes or chat-related functionality
 */
export function shouldStripChatParams({ fromPath, targetUrl }: { fromPath: string, targetUrl: string }): boolean {
  if (!isChatRoute(fromPath)) {
    return false // Not coming from a chat, nothing to strip
  }
  
  return !isNavigatingToChat(targetUrl) // Strip chat params unless we're going to another chat route
}

/**
 * Parameters that should be preserved when stripping chat route params
 * These are context parameters that should persist across navigation
 */
export const PRESERVED_CHAT_CONTEXT_PARAMS = [
  'matterId',
]
